/**
 * Download link interceptor for CSV files
 *
 * Intercepts clicks on <a download> elements to capture CSV data
 * before it's downloaded to the user's computer.
 */

import type { InterceptionConfig } from '../types/config';
import type { DataSource } from '../types/api';
import type {
  Interceptor,
  InterceptionHandler,
  InterceptedCSV,
} from './types';
import {
  isCSVMimeType,
  isCSVFilename,
  looksLikeCSV,
  hashCSV,
  suggestTableName,
} from './types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Download interceptor class
 */
export class DownloadInterceptor implements Interceptor {
  private active: boolean = false;
  private config: Required<InterceptionConfig>;
  private handler: InterceptionHandler;
  private clickHandler: (event: MouseEvent) => void;

  constructor(config: Required<InterceptionConfig>, handler: InterceptionHandler) {
    this.config = config;
    this.handler = handler;
    this.clickHandler = this.onLinkClick.bind(this);
  }

  /**
   * Start intercepting download links
   */
  start(): void {
    if (this.active) {
      console.warn('[DownloadInterceptor] Already active');
      return;
    }

    console.log('[DownloadInterceptor] Starting interception');

    // Listen to all click events in capture phase
    // This ensures we catch the event before it bubbles
    document.addEventListener('click', this.clickHandler, true);

    this.active = true;
  }

  /**
   * Stop intercepting
   */
  stop(): void {
    if (!this.active) {
      return;
    }

    console.log('[DownloadInterceptor] Stopping interception');

    // Remove event listener
    document.removeEventListener('click', this.clickHandler, true);

    this.active = false;
  }

  /**
   * Check if currently intercepting
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Handle link clicks
   */
  private onLinkClick(event: MouseEvent): void {
    // Find the anchor element
    let target = event.target as HTMLElement | null;

    // Traverse up to find <a> tag
    while (target && target.tagName !== 'A') {
      target = target.parentElement;
    }

    if (!target || target.tagName !== 'A') {
      return;
    }

    const anchor = target as HTMLAnchorElement;

    // Check if it has a download attribute
    if (!anchor.hasAttribute('download')) {
      return;
    }

    // Get download filename
    const downloadAttr = anchor.getAttribute('download') || '';

    // Check if it's a CSV file
    if (!isCSVFilename(downloadAttr) && !isCSVFilename(anchor.href)) {
      return;
    }

    console.log('[DownloadInterceptor] Download link clicked:', {
      href: anchor.href,
      download: downloadAttr,
    });

    // Try to intercept the data
    this.interceptDownload(anchor, downloadAttr).catch(error => {
      console.error('[DownloadInterceptor] Error intercepting download:', error);
    });

    // Don't prevent the download - let it proceed normally
  }

  /**
   * Intercept download data
   */
  private async interceptDownload(
    anchor: HTMLAnchorElement,
    filename: string
  ): Promise<void> {
    try {
      const href = anchor.href;

      // Check if it's a blob URL
      if (href.startsWith('blob:')) {
        await this.interceptBlobDownload(href, filename);
      } else if (href.startsWith('data:')) {
        await this.interceptDataUrlDownload(href, filename);
      } else {
        // It's a regular URL - this should be caught by fetch/XHR interceptor
        // But we can try to fetch it ourselves as a fallback
        await this.interceptUrlDownload(href, filename);
      }
    } catch (error) {
      console.error('[DownloadInterceptor] Error processing download:', error);
    }
  }

  /**
   * Intercept blob URL download
   */
  private async interceptBlobDownload(blobUrl: string, filename: string): Promise<void> {
    try {
      // Fetch the blob
      const response = await fetch(blobUrl);
      const blob = await response.blob();

      // Check if it's CSV
      if (!isCSVMimeType(blob.type) && !isCSVFilename(filename)) {
        return;
      }

      // Read as text
      const data = await blob.text();

      // Verify it looks like CSV
      if (!looksLikeCSV(data)) {
        console.log('[DownloadInterceptor] Blob does not look like CSV, skipping');
        return;
      }

      // Create intercepted CSV
      await this.createInterceptedCSV(data, filename, blobUrl, blob.type);
    } catch (error) {
      console.error('[DownloadInterceptor] Error intercepting blob download:', error);
    }
  }

  /**
   * Intercept data URL download
   */
  private async interceptDataUrlDownload(dataUrl: string, filename: string): Promise<void> {
    try {
      // Check if it's a CSV data URL
      if (!dataUrl.startsWith('data:text/csv') && !isCSVFilename(filename)) {
        return;
      }

      // Extract data from data URL
      const base64Match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
      if (base64Match) {
        // Decode base64
        const data = atob(base64Match[1]);

        // Verify it looks like CSV
        if (!looksLikeCSV(data)) {
          console.log('[DownloadInterceptor] Data URL does not look like CSV, skipping');
          return;
        }

        // Create intercepted CSV
        await this.createInterceptedCSV(data, filename, dataUrl, 'text/csv');
      } else {
        // Non-base64 data URL
        const commaIndex = dataUrl.indexOf(',');
        if (commaIndex !== -1) {
          const data = decodeURIComponent(dataUrl.substring(commaIndex + 1));

          // Verify it looks like CSV
          if (!looksLikeCSV(data)) {
            console.log('[DownloadInterceptor] Data URL does not look like CSV, skipping');
            return;
          }

          // Create intercepted CSV
          await this.createInterceptedCSV(data, filename, dataUrl, 'text/csv');
        }
      }
    } catch (error) {
      console.error('[DownloadInterceptor] Error intercepting data URL download:', error);
    }
  }

  /**
   * Intercept regular URL download
   */
  private async interceptUrlDownload(url: string, filename: string): Promise<void> {
    try {
      // Only process if it's a CSV file
      if (!isCSVFilename(filename) && !isCSVFilename(url)) {
        return;
      }

      // Fetch the file
      const response = await fetch(url);
      const data = await response.text();

      // Verify it looks like CSV
      if (!looksLikeCSV(data)) {
        console.log('[DownloadInterceptor] URL content does not look like CSV, skipping');
        return;
      }

      const contentType = response.headers.get('Content-Type') || 'text/csv';

      // Create intercepted CSV
      await this.createInterceptedCSV(data, filename, url, contentType);
    } catch (error) {
      console.error('[DownloadInterceptor] Error intercepting URL download:', error);
    }
  }

  /**
   * Create an intercepted CSV object and call handler
   */
  private async createInterceptedCSV(
    data: string,
    filename: string,
    url: string,
    contentType: string
  ): Promise<void> {
    // Skip empty data
    if (!data || data.trim().length === 0) {
      return;
    }

    const suggestedName = suggestTableName(filename);

    // Create intercepted CSV object
    const intercepted: InterceptedCSV = {
      id: uuidv4(),
      data,
      size: data.length,
      source: {
        type: 'blob',
        url,
        filename: filename || undefined,
        contentType,
      },
      interceptedVia: 'download',
      timestamp: Date.now(),
      hash: hashCSV(data),
      suggestedName,
    };

    console.log('[DownloadInterceptor] CSV download intercepted:', {
      filename,
      size: data.length,
      suggestedName,
    });

    // Call handler
    await this.handler(intercepted);
  }
}

/**
 * Create a download interceptor
 */
export function createDownloadInterceptor(
  config: Required<InterceptionConfig>,
  handler: InterceptionHandler
): Interceptor {
  return new DownloadInterceptor(config, handler);
}
